import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, Mic, Pause, Play, Sparkles, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import { formatApiError } from '@/lib/formatApiError';
import { commitVoiceDesign, fetchVoiceDesignBrief, requestVoiceDesign } from '@/lib/creationPlan';

const FALLBACK_LANGUAGES = [
  { id: 'en', label: 'English' },
  { id: 'hi', label: 'Hindi' },
  { id: 'es', label: 'Spanish' },
  { id: 'fr', label: 'French' },
  { id: 'de', label: 'German' },
  { id: 'ta', label: 'Tamil' },
  { id: 'te', label: 'Telugu' },
  { id: 'bn', label: 'Bengali' },
  { id: 'mr', label: 'Marathi' },
];

const FALLBACK_GENDERS = [
  { id: 'female', label: 'Female' },
  { id: 'male', label: 'Male' },
  { id: 'non_binary', label: 'Non-binary' },
  { id: 'not_specified', label: 'Not specified' },
];

const MAX_REFERENCE_BYTES = 3.5 * 1024 * 1024;
const MAX_RECORD_MS = 15000;

function previewKey(slot, generatedVoiceId) {
  return `design:${slot}:${generatedVoiceId}`;
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error || new Error('Could not read audio'));
    reader.readAsDataURL(blob);
  });
}

function pickRecorderMime() {
  if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) return '';
  if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) return 'audio/webm;codecs=opus';
  if (MediaRecorder.isTypeSupported('audio/webm')) return 'audio/webm';
  if (MediaRecorder.isTypeSupported('audio/mp4')) return 'audio/mp4';
  return '';
}

export function useDesignedVoiceSamples() {
  const audioRef = useRef(null);
  const [playingKey, setPlayingKey] = useState('');

  const stop = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlayingKey('');
  }, []);

  useEffect(
    () => () => {
      if (audioRef.current) audioRef.current.pause();
    },
    [],
  );

  const toggle = useCallback(
    (slot, preview) => {
      const key = previewKey(slot, preview.generated_voice_id);
      if (playingKey === key) {
        stop();
        return;
      }
      stop();
      const b64 = preview.audio_base_64;
      if (!b64) return;
      const mime = preview.media_type || 'audio/mpeg';
      const audio = new Audio(`data:${mime};base64,${b64}`);
      audioRef.current = audio;
      audio.onended = () => setPlayingKey((k) => (k === key ? '' : k));
      setPlayingKey(key);
      audio.play().catch(() => setPlayingKey(''));
    },
    [playingKey, stop],
  );

  return { playingKey, toggle, stop, previewKey };
}

const selectClass =
  'w-full rounded-lg bg-white/[0.04] border border-white/15 px-2 py-1.5 text-[11px] text-[#E5E7EB] focus:outline-none focus:border-[#A78BFA]/60 cursor-pointer';

export function DesignedVoicePreviews({
  planId,
  slot = 'narrator',
  title = 'Design a voice',
  onCommitted,
  compact = false,
  defaultLanguage = '',
  defaultGender = '',
}) {
  const [supported, setSupported] = useState(true);
  const [configured, setConfigured] = useState(true);
  const [briefLoading, setBriefLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [prompt, setPrompt] = useState('');
  const [language, setLanguage] = useState(defaultLanguage || 'en');
  const [gender, setGender] = useState(defaultGender || 'female');
  const [languages, setLanguages] = useState(FALLBACK_LANGUAGES);
  const [genders, setGenders] = useState(FALLBACK_GENDERS);
  const [suggestedLanguage, setSuggestedLanguage] = useState('');
  const [suggestedGender, setSuggestedGender] = useState('');
  const [previews, setPreviews] = useState([]);
  const [committing, setCommitting] = useState('');
  const [referenceName, setReferenceName] = useState('');
  const [referenceBase64, setReferenceBase64] = useState('');
  const [referenceUrl, setReferenceUrl] = useState('');
  const [recording, setRecording] = useState(false);
  const [recordSeconds, setRecordSeconds] = useState(0);
  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const mediaChunksRef = useRef([]);
  const mediaStreamRef = useRef(null);
  const recordTimerRef = useRef(null);
  const recordStartedAtRef = useRef(0);
  const samples = useDesignedVoiceSamples();

  const clearReference = useCallback(() => {
    setReferenceName('');
    setReferenceBase64('');
    setReferenceUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return '';
    });
  }, []);

  const setReferenceFromBlob = useCallback(
    async (blob, name) => {
      if (!blob || !blob.size) {
        toast.error('No audio captured');
        return;
      }
      if (blob.size > MAX_REFERENCE_BYTES) {
        toast.error('Reference clip is too large — keep it under ~3.5MB (about 5–15 seconds).');
        return;
      }
      try {
        const b64 = await blobToBase64(blob);
        clearReference();
        setReferenceBase64(b64);
        setReferenceName(name || 'Reference clip');
        setReferenceUrl(URL.createObjectURL(blob));
      } catch {
        toast.error('Could not read that audio file');
      }
    },
    [clearReference],
  );

  const stopRecording = useCallback(() => {
    const rec = mediaRecorderRef.current;
    if (rec && rec.state !== 'inactive') {
      try {
        rec.stop();
      } catch {
        /* ignore */
      }
    }
    if (recordTimerRef.current) {
      clearInterval(recordTimerRef.current);
      recordTimerRef.current = null;
    }
    setRecording(false);
  }, []);

  useEffect(
    () => () => {
      stopRecording();
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((t) => t.stop());
        mediaStreamRef.current = null;
      }
      if (referenceUrl) URL.revokeObjectURL(referenceUrl);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- cleanup only on unmount
    [],
  );

  const startRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error('Microphone not available in this browser');
      return;
    }
    if (recording) {
      stopRecording();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      mediaChunksRef.current = [];
      const mimeType = pickRecorderMime();
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      mediaRecorderRef.current = recorder;
      recorder.ondataavailable = (e) => {
        if (e.data?.size) mediaChunksRef.current.push(e.data);
      };
      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        mediaStreamRef.current = null;
        const blob = new Blob(mediaChunksRef.current, { type: recorder.mimeType || 'audio/webm' });
        const secs = Math.max(1, Math.round((Date.now() - recordStartedAtRef.current) / 1000));
        await setReferenceFromBlob(blob, `Recording (${secs}s)`);
      };
      recorder.start(250);
      recordStartedAtRef.current = Date.now();
      setRecordSeconds(0);
      setRecording(true);
      recordTimerRef.current = setInterval(() => {
        const elapsed = Date.now() - recordStartedAtRef.current;
        setRecordSeconds(Math.floor(elapsed / 1000));
        if (elapsed >= MAX_RECORD_MS) stopRecording();
      }, 250);
      toast.message('Recording… tap Stop when done (max 15s)');
    } catch {
      toast.error('Could not access microphone');
    }
  };

  const onPickFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!String(file.type || '').startsWith('audio/') && !/\.(mp3|wav|m4a|ogg|webm|aac|flac)$/i.test(file.name || '')) {
      toast.error('Choose an audio file (mp3, wav, m4a, webm…)');
      return;
    }
    await setReferenceFromBlob(file, file.name || 'Uploaded clip');
  };

  const loadBrief = useCallback(async () => {
    if (!planId) {
      setBriefLoading(false);
      setSupported(false);
      return;
    }
    setBriefLoading(true);
    try {
      const data = await fetchVoiceDesignBrief(planId, { slot });
      setSupported(data.supported !== false);
      setConfigured(data.configured !== false);
      const langOpts = Array.isArray(data.languages) && data.languages.length ? data.languages : FALLBACK_LANGUAGES;
      const genderOpts = Array.isArray(data.genders) && data.genders.length ? data.genders : FALLBACK_GENDERS;
      setLanguages(langOpts);
      setGenders(genderOpts);
      const suggestedLang = data.defaults?.language || defaultLanguage || 'en';
      const suggestedGen = data.defaults?.gender || defaultGender || 'female';
      setSuggestedLanguage(suggestedLang);
      setSuggestedGender(suggestedGen);
      setLanguage(data.design?.language || suggestedLang);
      setGender(data.design?.gender || suggestedGen);
      const suggested = data.brief?.voice_description || data.design?.description || '';
      setPrompt((prev) => prev || suggested);
      if (Array.isArray(data.design?.previews) && data.design.previews.length) {
        setPreviews(data.design.previews);
      }
    } catch {
      setSupported(true);
      if (defaultLanguage) setLanguage(defaultLanguage);
      if (defaultGender) setGender(defaultGender);
    } finally {
      setBriefLoading(false);
    }
  }, [planId, slot, defaultLanguage, defaultGender]);

  useEffect(() => {
    loadBrief();
  }, [loadBrief]);

  const design = async () => {
    setLoading(true);
    setError('');
    samples.stop();
    try {
      const data = await requestVoiceDesign(planId, {
        slot,
        voiceDescription: prompt.trim(),
        language,
        gender,
        referenceAudioBase64: referenceBase64 || undefined,
      });
      setPreviews(data.previews || []);
      if (data.design?.description) setPrompt(data.design.description);
      if (data.design?.language) setLanguage(data.design.language);
      if (data.design?.gender) setGender(data.design.gender);
      if (!(data.previews || []).length) {
        setError('No previews came back. Try a more specific voice prompt.');
      }
    } catch (err) {
      setPreviews([]);
      setError(formatApiError(err, 'Voice design unavailable'));
    } finally {
      setLoading(false);
    }
  };

  const commit = async (preview, index) => {
    const gid = preview.generated_voice_id;
    if (!gid) return;
    setCommitting(gid);
    samples.stop();
    try {
      const { plan, voice } = await commitVoiceDesign(planId, {
        slot,
        generatedVoiceId: gid,
        voiceName: `Designed ${index + 1}`,
      });
      onCommitted?.(voice, plan);
    } catch (err) {
      toast.error(formatApiError(err, 'Could not save this designed voice'));
    } finally {
      setCommitting('');
    }
  };

  if (!planId || !supported) return null;

  const langLabel = languages.find((l) => l.id === suggestedLanguage)?.label || suggestedLanguage;
  const genderLabel = genders.find((g) => g.id === suggestedGender)?.label || suggestedGender;

  return (
    <div className="space-y-1.5" data-testid="create-plan-designed-voices">
      <p className="text-[11px] text-[#9CA3AF] flex items-center gap-1">
        <Sparkles className="w-3 h-3 text-[#A78BFA]" />
        {title}
      </p>
      {briefLoading ? (
        <p className="text-[11px] text-[#9CA3AF] flex items-center gap-1.5">
          <Loader2 className="w-3 h-3 animate-spin" /> Preparing a voice prompt…
        </p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2">
            <label className="space-y-0.5 min-w-0">
              <span className="text-[10px] text-[#9CA3AF]">Language</span>
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                data-testid="create-plan-designed-voice-language"
                className={selectClass}
              >
                {languages.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.label}
                    {opt.id === suggestedLanguage ? ' (suggested)' : ''}
                  </option>
                ))}
              </select>
            </label>
            <label className="space-y-0.5 min-w-0">
              <span className="text-[10px] text-[#9CA3AF]">Gender</span>
              <select
                value={gender}
                onChange={(e) => setGender(e.target.value)}
                data-testid="create-plan-designed-voice-gender"
                className={selectClass}
              >
                {genders.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.label}
                    {opt.id === suggestedGender ? ' (suggested)' : ''}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {(langLabel || genderLabel) && !compact ? (
            <p className="text-[10px] text-[#6B7280]" data-testid="create-plan-designed-voice-defaults-hint">
              Suggested from your plan: {langLabel}
              {genderLabel ? ` · ${genderLabel}` : ''}
            </p>
          ) : null}
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={compact ? 2 : 3}
            maxLength={1000}
            placeholder="Describe age, accent, timbre, and delivery — then design a voice."
            data-testid="create-plan-designed-voice-prompt"
            className="w-full rounded-xl bg-white/[0.04] border border-white/15 px-2.5 py-1.5 text-[11px] text-[#E5E7EB] placeholder:text-[#6B7280] focus:outline-none focus:border-[#A78BFA]/60 resize-y min-h-[44px]"
          />
          <div className="rounded-xl border border-dashed border-white/15 bg-white/[0.02] px-2.5 py-2 space-y-1.5">
            <p className="text-[10px] text-[#9CA3AF]">Optional reference clip (upload or record ~5–15s)</p>
            <div className="flex flex-wrap items-center gap-1.5">
              <input
                ref={fileInputRef}
                type="file"
                accept="audio/*,.mp3,.wav,.m4a,.ogg,.webm,.aac,.flac"
                className="hidden"
                data-testid="create-plan-designed-voice-upload-input"
                onChange={onPickFile}
              />
              <button
                type="button"
                disabled={loading || recording}
                onClick={() => fileInputRef.current?.click()}
                data-testid="create-plan-designed-voice-upload"
                className="px-2.5 py-1 rounded-full text-[10px] cursor-pointer border border-white/20 text-[#E5E7EB] hover:border-[#A78BFA]/50 hover:text-white inline-flex items-center gap-1 disabled:opacity-50"
              >
                <Upload className="w-3 h-3" /> Upload
              </button>
              <button
                type="button"
                disabled={loading}
                onClick={startRecording}
                data-testid="create-plan-designed-voice-record"
                className={`px-2.5 py-1 rounded-full text-[10px] cursor-pointer border inline-flex items-center gap-1 disabled:opacity-50 ${
                  recording
                    ? 'border-rose-400/60 text-rose-300 bg-rose-500/10'
                    : 'border-white/20 text-[#E5E7EB] hover:border-[#A78BFA]/50 hover:text-white'
                }`}
              >
                <Mic className="w-3 h-3" />
                {recording ? `Stop (${recordSeconds}s)` : 'Record'}
              </button>
              {referenceName ? (
                <span
                  className="inline-flex items-center gap-1 max-w-full px-2 py-0.5 rounded-full bg-[#A78BFA]/15 text-[#C4B5FD] text-[10px]"
                  data-testid="create-plan-designed-voice-reference"
                >
                  <span className="truncate max-w-[140px]">{referenceName}</span>
                  {referenceUrl ? (
                    <a href={referenceUrl} target="_blank" rel="noreferrer" className="underline opacity-80 hover:opacity-100">
                      play
                    </a>
                  ) : null}
                  <button
                    type="button"
                    aria-label="Clear reference clip"
                    onClick={clearReference}
                    className="p-0.5 rounded hover:bg-white/10 cursor-pointer"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ) : (
                <span className="text-[10px] text-[#6B7280]">No reference yet</span>
              )}
            </div>
          </div>
          <button
            type="button"
            disabled={loading || !configured || recording}
            onClick={design}
            data-testid="create-plan-design-voice"
            className="px-3 py-1.5 rounded-full text-xs cursor-pointer border border-[#A78BFA]/50 text-[#C4B5FD] hover:bg-[#A78BFA]/15 inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
            {previews.length ? 'Redesign voice' : 'Design a voice'}
          </button>
          {!configured ? (
            <p className="text-[10px] text-amber-300">ElevenLabs is not configured on this server.</p>
          ) : null}
        </>
      )}
      {error ? <p className="text-[11px] text-amber-300">{error}</p> : null}
      {previews.map((preview, i) => {
        const key = previewKey(slot, preview.generated_voice_id);
        const playing = samples.playingKey === key;
        const busy = committing === preview.generated_voice_id;
        return (
          <div
            key={key}
            className="flex items-center gap-3 rounded-xl border border-[#A78BFA]/30 bg-[#A78BFA]/5 px-3 py-2"
            data-testid="create-plan-designed-voice-row"
          >
            <button
              type="button"
              onClick={() => samples.toggle(slot, preview)}
              className="w-8 h-8 rounded-full shrink-0 flex items-center justify-center cursor-pointer border border-[#A78BFA]/40 bg-[#A78BFA]/10 text-[#C4B5FD]"
            >
              {playing ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5 translate-x-px" />}
            </button>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold text-white truncate">Designed option {i + 1}</p>
              {!compact && preview.language ? (
                <p className="text-[10px] text-[#9CA3AF] truncate">{preview.language}</p>
              ) : null}
            </div>
            <button
              type="button"
              disabled={Boolean(busy)}
              onClick={() => commit(preview, i)}
              data-testid="create-plan-designed-voice-use"
              className="px-2.5 py-1 rounded-full text-[10px] font-semibold border border-[#A78BFA]/50 text-[#C4B5FD] hover:bg-[#A78BFA]/15 disabled:opacity-50"
            >
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Create & use'}
            </button>
          </div>
        );
      })}
    </div>
  );
}
